import { FontFamily } from '@modern-treasury/modern-treasury-js'
import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import Button from 'ext-ui/components/Button/Button'
import Processing from 'ext-ui/components/Processing/Processing'
import { base_colorAction, base_contentBackground } from 'ext-ui/style/colors'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import useModernTreasurySdk, {
  MtEmbeddableFlow,
} from 'src/hooks/useModernTreasurySdk'
import { useTranslate } from 'src/localization/Trns'
import {
  AccountCollectionFlowCompletionResponse,
  AccountCollectionFlowResponse,
} from 'src/queries/payments/payments.types'

import style from './MtAccountCollection.style'

type ImmediateCompletionMode = {
  /** Links the collected account once MT's own `onSuccess` fires — pass
   * `useCompleteAccountCollectionFlow`'s `mutateAsync` (it takes the flow id).
   * MT fires no completion webhook, so the app drives the link via
   * `POST /account-collection-flow/{flowId}/complete`. Resolves `COMPLETED`
   * (HTTP 200 — linked; the component then calls `onLinked`) or `PENDING`
   * (HTTP 202 — MT hasn't finished; the embed shows a still-linking note).
   * Rejects on 4xx/5xx. A terminal 409 (expired/cancelled) releases the parent
   * modal lock so the user can close and start a replacement flow; transient
   * failures can retry this flow id. */
  completeFlow: (
    flowId: string,
  ) => Promise<AccountCollectionFlowCompletionResponse>
  /** Called once the funding account is linked (completion resolved
   * `COMPLETED`). The result lets the caller transition directly into
   * verification without waiting for a refetch. */
  onLinked: (result: AccountCollectionFlowCompletionResponse) => void
  /** Called once `/complete` first returns PENDING. The parent uses this to
   * keep the account card non-actionable even if the modal is dismissed. */
  onPending?: () => void
  /** Called when `/complete` returns a terminal 409. The parent releases its
   * pending lock, allowing this modal to close and a replacement flow to start. */
  onTerminal?: () => void
  onCollectionSubmitted?: never
}

type DeferredConfirmationMode<TFlow extends AccountCollectionFlowResponse> = {
  /** Advances the owning flow after MT accepts the hosted form. The callback
   * receives only the flow response created by our server; the provider's SDK
   * callback payload is deliberately ignored. */
  onCollectionSubmitted: (flow: TFlow) => void
  completeFlow?: never
  onLinked?: never
  onPending?: never
  onTerminal?: never
}

export type MtAccountCollectionProps<
  TFlow extends AccountCollectionFlowResponse = AccountCollectionFlowResponse,
> = {
  /** Bootstraps the embedded flow — pass `useCreateAccountCollectionFlow`'s
   * `mutateAsync` (org funding) or `useCreateVendorAccountCollectionFlow`'s
   * `mutateAsync` bound to a vendor id (vendor destination). Called once on
   * mount. */
  createFlow: () => Promise<TFlow>
  onCancel?: () => void
  /** Renders the "Add your bank account" title + subtitle above the embed.
   * Defaults to true: on BankAccountsPage this is the only visible title its
   * BaseModal has (the modal supplies just an aria-label). Pass false where
   * the host surface already titles the step — AddVendorFlyout's own header
   * says "Add ACH Payment Details" and describes the same action, so the
   * heading is duplicate copy there. */
  showHeading?: boolean
  /** Drops the component's own 40px padding. Defaults to false: on
   * BankAccountsPage that padding is the BaseModal's inner spacing. Pass true
   * where the host already pads its content — AddVendorFlyout's field column
   * does, and the two stacked made the MT form visibly narrower than the
   * flyout's own Step 1 inputs. */
  flush?: boolean
} & (ImmediateCompletionMode | DeferredConfirmationMode<TFlow>)

type Phase =
  | 'creating-flow'
  | 'loading-sdk'
  | 'ready'
  | 'completing'
  | 'pending-link'
  | 'error'

/**
 * What failed, kept separate from its rendered copy: effects set only this
 * (never a translated string — `translate()`'s return value has a fresh
 * identity every render, so it must never sit in an effect dependency array,
 * see `src/AGENTS.md` on network-effect triggers). The string is resolved at
 * render time from `errorKind` instead.
 */
type ErrorKind =
  | 'create-flow'
  | 'load'
  | 'complete'
  | 'complete-terminal'
  | null

const COMPLETION_RETRY_DELAYS_MS = [1_000, 2_000, 4_000] as const

/**
 * Routing-free feature component (props only — no react-router, no query
 * hooks; see `src/AGENTS.md` layering) that mounts Modern Treasury's own
 * embedded Account Collection Flow in place of our own routing/account-number
 * form: the customer types their bank details into MT's hosted iframe and MT
 * creates the ExternalAccount. This component's job is the handshake — call
 * `createFlow()`, load the MT SDK with the returned `publishableKey`, mount the
 * flow with the returned `clientToken` — and then, once MT's `onSuccess` fires,
 * link the account via `completeFlow(flowId)` (MT fires no webhook), calling
 * `onLinked(result)` on a `COMPLETED` result. US-ACH only for now.
 */
export default function MtAccountCollection<
  TFlow extends AccountCollectionFlowResponse = AccountCollectionFlowResponse,
>(props: MtAccountCollectionProps<TFlow>): React.ReactElement {
  const { createFlow, onCancel, showHeading = true, flush = false } = props
  const translate = useTranslate()
  const containerRef = useRef<HTMLDivElement | null>(null)

  const [phase, setPhase] = useState<Phase>('creating-flow')
  const [errorKind, setErrorKind] = useState<ErrorKind>(null)
  const [canRetryCompletion, setCanRetryCompletion] = useState(false)
  const [flowResponse, setFlowResponse] = useState<TFlow | null>(null)

  // Stash the latest callbacks in a ref so the "call once on mount" effect
  // below doesn't re-fire just because the parent re-renders with a fresh
  // function identity — mirrors `ChargebeeCheckoutModal`'s `handlers` ref.
  const callbacksRef = useRef({
    createFlow,
    completeFlow: props.completeFlow,
    onLinked: props.onLinked,
    onPending: props.onPending,
    onTerminal: props.onTerminal,
    onCollectionSubmitted: props.onCollectionSubmitted,
  })
  useLayoutEffect(() => {
    callbacksRef.current = {
      createFlow,
      completeFlow: props.completeFlow,
      onLinked: props.onLinked,
      onPending: props.onPending,
      onTerminal: props.onTerminal,
      onCollectionSubmitted: props.onCollectionSubmitted,
    }
  })

  // A duplicate SDK onSuccess must never start a parallel completion sequence.
  // Sequential retries are driven only by `attemptCompletion`, always with the
  // original flow id captured from createFlow.
  const submissionStartedRef = useRef(false)
  const completionFlowIdRef = useRef<string | null>(null)
  const completionInFlightRef = useRef(false)
  const completionFinishedRef = useRef(false)
  const pendingNotifiedRef = useRef(false)
  const autoRetryIndexRef = useRef(0)
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
        retryTimeoutRef.current = null
      }
    }
  }, [])

  const attemptCompletion = useCallback(
    async (flowId: string): Promise<void> => {
      if (
        !mountedRef.current ||
        completionInFlightRef.current ||
        completionFinishedRef.current
      ) {
        return
      }

      completionInFlightRef.current = true
      setErrorKind(null)
      setCanRetryCompletion(false)
      setPhase('completing')

      try {
        const completeFlow = callbacksRef.current.completeFlow
        if (!completeFlow) return
        const result = await completeFlow(flowId)
        if (!mountedRef.current) return

        if (result.status === 'COMPLETED') {
          completionFinishedRef.current = true
          callbacksRef.current.onLinked?.(result)
          return
        }

        if (!pendingNotifiedRef.current) {
          pendingNotifiedRef.current = true
          callbacksRef.current.onPending?.()
        }
        setPhase('pending-link')

        const nextDelay = COMPLETION_RETRY_DELAYS_MS[autoRetryIndexRef.current]
        if (nextDelay === undefined) {
          setCanRetryCompletion(true)
          return
        }
        autoRetryIndexRef.current += 1
        retryTimeoutRef.current = setTimeout(() => {
          retryTimeoutRef.current = null
          void attemptCompletion(flowId)
        }, nextDelay)
      } catch (error) {
        if (!mountedRef.current) return
        const isTerminal =
          typeof error === 'object' &&
          error !== null &&
          'status' in error &&
          error.status === 409
        if (isTerminal) {
          setErrorKind('complete-terminal')
          setCanRetryCompletion(false)
          callbacksRef.current.onTerminal?.()
        } else {
          setErrorKind('complete')
          setCanRetryCompletion(true)
        }
        setPhase('error')
      } finally {
        completionInFlightRef.current = false
      }
    },
    [],
  )

  const retryCompletion = useCallback((): void => {
    const flowId = completionFlowIdRef.current
    if (!flowId || completionFinishedRef.current) return
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current)
      retryTimeoutRef.current = null
    }
    autoRetryIndexRef.current = 0
    void attemptCompletion(flowId)
  }, [attemptCompletion])

  // Create the flow exactly once per mount. Deliberately empty deps — this
  // must not use `createFlow`'s identity as a re-trigger (`src/AGENTS.md`:
  // network effects must not fire on identity churn from a re-render); only a
  // real remount should call this again.
  useEffect(() => {
    let cancelled = false
    setPhase('creating-flow')
    setErrorKind(null)
    callbacksRef.current
      .createFlow()
      .then((response) => {
        if (cancelled) return
        setFlowResponse(response)
        setPhase('loading-sdk')
      })
      .catch(() => {
        if (cancelled) return
        setErrorKind('create-flow')
        setPhase('error')
      })
    return (): void => {
      cancelled = true
    }
  }, [])

  const {
    status: sdkStatus,
    mtInstance,
    publishableKey: resolvedPublishableKey,
  } = useModernTreasurySdk({
    publishableKey: flowResponse?.publishableKey ?? '',
  })

  // `mtInstance` is a fresh object reference on some hook implementations
  // (defensive here — the real `useModernTreasurySdk` returns an instance that
  // is stable per publishable key, but nothing should rely on that to avoid a
  // double-mount). Read the latest one via a ref instead of an effect
  // dependency; the mount effect below keys only on the stable primitives that
  // describe the real transition (`sdkStatus`, `resolvedPublishableKey` — see
  // `src/AGENTS.md` on network-effect triggers).
  const mtInstanceRef = useRef(mtInstance)
  useLayoutEffect(() => {
    mtInstanceRef.current = mtInstance
  })

  useEffect(() => {
    if (sdkStatus !== 'error') return
    setErrorKind('load')
    setPhase('error')
  }, [sdkStatus])

  useEffect(() => {
    if (sdkStatus !== 'ready' || !flowResponse) return
    // Mount only once the loaded SDK actually corresponds to THIS flow's
    // publishable key. Otherwise a `ready` instance left over from a previous
    // key (org-specific keys / key rotation) would mount this flow's
    // `clientToken` against the wrong SDK. `resolvedPublishableKey` is a stable
    // primitive, so it is a safe effect dep (unlike the instance identity).
    if (resolvedPublishableKey !== flowResponse.publishableKey) return
    const instance = mtInstanceRef.current
    const container = containerRef.current
    if (!instance || !container) return

    let flow: MtEmbeddableFlow | undefined
    // `onError` can fire synchronously during `mount`; if it does, we must NOT
    // then unconditionally flip to `ready` and clobber the error state.
    let mountErrored = false
    try {
      flow = instance.createEmbeddableFlow({
        clientToken: flowResponse.clientToken,
        // Brand-match MT's iframe to ext-ui — the only in-iframe styling MT
        // exposes: primary accent (base_colorAction) + surface
        // (base_contentBackground), resolved from the active brand theme. Rubik
        // isn't in MT's font enum, so DM Sans is the closest offered face.
        variables: {
          colorPrimary: base_colorAction,
          colorBackground: base_contentBackground,
          fontFamily: FontFamily.dmSans,
        },
        // MT supports only en / es-419 / auto — no French. `auto` lets MT match
        // the browser where it can (a fr-CA user still falls back to English).
        locale: 'auto',
        onSuccess: (): void => {
          // MT finished collecting the bank details. Completion is app-driven BY
          // DESIGN: MT fires no completion webhook, so link it server-side via a
          // `POST /complete` carrying this flow's id (captured in this closure).
          // The `completeFlow` mutation hook owns cache invalidation
          // (`paymentsKeys.connection`) and App.tsx binds the resulting
          // `PAYMENT_CONNECTION` Pusher event for cross-tab refresh — do NOT
          // invalidate/refresh the connection from here.
          if (submissionStartedRef.current) return
          submissionStartedRef.current = true
          const onCollectionSubmitted =
            callbacksRef.current.onCollectionSubmitted
          if (onCollectionSubmitted) {
            onCollectionSubmitted(flowResponse)
            return
          }
          const flowId = flowResponse.flowId
          completionFlowIdRef.current = flowId
          void attemptCompletion(flowId)
        },
        onError: (): void => {
          mountErrored = true
          setErrorKind('load')
          setPhase('error')
        },
      })
      flow.mount(container)
    } catch {
      // createEmbeddableFlow / mount can throw (expired token, invalid domain,
      // SDK failure). Tear down any partially-created flow and surface an error
      // instead of crashing the page.
      flow?.unmount?.()
      setErrorKind('load')
      setPhase('error')
      return
    }

    // Advance to `ready` only after a successful mount, and never over a
    // synchronous `onError` that already moved us to the error phase.
    if (!mountErrored) {
      setPhase('ready')
    }

    return (): void => {
      flow?.unmount?.()
    }
  }, [sdkStatus, flowResponse, resolvedPublishableKey, attemptCompletion])

  const isLoading =
    phase === 'creating-flow' ||
    phase === 'loading-sdk' ||
    phase === 'completing'
  const loadingText =
    phase === 'completing'
      ? translate('Linking your bank account…')
      : translate('Loading secure bank details form…')
  const errorMessage =
    errorKind === 'create-flow'
      ? translate(
          'We could not start the bank account setup. Please try again.',
        )
      : errorKind === 'load'
        ? translate(
            'We could not load the bank details form. Please try again.',
          )
        : errorKind === 'complete'
          ? translate(
              'We could not finish linking your bank account. Please try again.',
            )
          : errorKind === 'complete-terminal'
            ? translate(
                'This bank account setup can no longer be completed. Close this window and start again.',
              )
            : null
  const canCancel =
    Boolean(onCancel) &&
    phase !== 'completing' &&
    phase !== 'pending-link' &&
    (!pendingNotifiedRef.current || errorKind === 'complete-terminal')

  return (
    <div
      className={`MtAccountCollection${flush ? ' MtAccountCollection--flush' : ''}`}
      css={style}
    >
      {showHeading && (
        <div className="MtAccountCollection-heading">
          <p className="MtAccountCollection-title">
            {translate('Add your bank account')}
          </p>
          <p className="MtAccountCollection-subtitle">
            {translate('Enter your bank account details securely below.')}
          </p>
        </div>
      )}

      {phase === 'error' && errorMessage && (
        <AlertPanel variant="severe">{errorMessage}</AlertPanel>
      )}

      {isLoading && (
        <div
          className="MtAccountCollection-loading"
          role="status"
          aria-live="polite"
        >
          <Processing />
          <span className="MtAccountCollection-loading-text">
            {loadingText}
          </span>
        </div>
      )}

      {phase === 'pending-link' && (
        <div
          className="MtAccountCollection-loading"
          role="status"
          aria-live="polite"
        >
          <span className="MtAccountCollection-loading-text">
            {translate(
              'Your bank account is still being linked. This can take a moment.',
            )}
          </span>
        </div>
      )}

      <div
        ref={containerRef}
        className="MtAccountCollection-container"
        role="group"
        aria-label={translate('Bank account details')}
        hidden={phase !== 'ready'}
        data-testid="mt-account-collection-container"
      />

      {(canRetryCompletion || canCancel) && (
        <div className="MtAccountCollection-actions">
          {canRetryCompletion && (
            <Button variant="secondary" onClick={retryCompletion}>
              {translate('Try again')}
            </Button>
          )}
          {canCancel && onCancel && (
            <Button
              variant="text"
              onClick={onCancel}
              data-testid="mt-account-collection-cancel"
            >
              {translate(
                errorKind === 'complete-terminal' ? 'Close' : 'Cancel',
              )}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
